import type {
    CoreStreams,
    DraggedAssets,
    InputFile,
    AddFiles,
    ResourceForType,
} from "@/client/core/Core.api";
import type { LabsResourceType } from "@/client/core/platform-types/labs.api";
import {
    createBaseAssetMetadata,
    type AssetWithArtworkUrl,
    type DraggedAssetTransform,
    type LabsBaseAssetMetadata,
    type LabsInputMetadata,
} from "@/client/core/platform-types/labs.api";
import { AssetCategorySlug } from "@/client/core/platform-types/types";
import { resolveDraggedBpm } from "@/client/core/services/dragged-bpm";
import type { ModifiedFileName } from "@/client/core/services/modified-file-name";
import { modifiedFileNameCandidates } from "@/client/core/services/modified-file-name";
import { decodeAudioFile } from "@/client/utils/decodeAudio";
import { createInputAudioTypeFromAsset } from "@/client/utils/createInputAudioType";

/**
 * Duration of the audio in hand, which is what the catalog's own duration gets
 * measured against. Injectable so tests can skip the decode.
 */
export type GetAudioDuration = (file: File) => Promise<number | undefined>;

const decodeDuration: GetAudioDuration = async (file) => {
    try {
        const audioBuffer = await decodeAudioFile(file);
        return audioBuffer.duration;
    } catch {
        // An undecodable file still deserves whatever metadata we can match;
        // it just loses the duration cross-check.
        return undefined;
    }
};

interface MatchedAsset {
    /** The catalog asset the dragged file came from, when we can name it. */
    asset: AssetWithArtworkUrl | undefined;
    /** What the filename says the plugin did to the audio on its way over. */
    modified: ModifiedFileName;
    /** What the host said it did, when the host is new enough to say. */
    reported: DraggedAssetTransform | undefined;
}

/**
 * Match a dragged file back to the asset it came from, reading the render
 * suffixes the plugin added (see `modified-file-name.ts`) as we go. The first
 * reading of the name that matches an asset wins, so a warped copy of a sample
 * whose catalog name already ends in `_128bpm.wav` keeps both its identity and
 * the tempo it was actually rendered to.
 */
const findMatchedAsset = (file: File, dragged: DraggedAssets): MatchedAsset => {
    const candidates = modifiedFileNameCandidates(file.name);
    const reported = dragged.transform;

    for (const modified of candidates) {
        const asset = dragged.assets.find((a) => a.asset.name?.endsWith(modified.name));
        if (asset !== undefined) {
            return { asset, modified, reported };
        }
    }

    // Nothing matched — a file dropped from Finder rather than dragged out of
    // the browser, say. The suffixes still describe this file, since they came
    // off its own name, so keep them. The host's transform does not: it belongs
    // to whatever was dragged, and a drag that was started and then cancelled
    // leaves its payload in place until the next add. Applying it here would
    // stamp an unrelated upload with that drag's tempo and key.
    return {
        asset: undefined,
        modified: candidates[candidates.length - 1],
        reported: undefined,
    };
};

/**
 * The catalog asset describes the file in the catalog; the file in hand may
 * have been re-rendered on its way over. Where they disagree the file in hand
 * wins — Magic Fit and Blend send this BPM straight to the model, so a stale
 * tempo is silently wrong rather than visibly broken. `dragged-bpm.ts` ranks
 * the sources, with the host's own values on top.
 *
 * Key follows the same order. The host states the key it transposed to, since
 * it owns the "transpose all to" target. Failing that, a pitch shift we can
 * only see in the filename leaves the key unknown rather than wrong — the
 * semitone count is there but the landing key isn't. `chordType` survives
 * either way: transposing a sample doesn't turn a minor loop major.
 */
const createModifiedAssetMetadata = (
    fileName: string,
    { asset, modified, reported }: MatchedAsset,
    renderedDuration_s: number | undefined,
): LabsInputMetadata<LabsBaseAssetMetadata> => {
    const base = createBaseAssetMetadata(fileName, asset);
    const sample = asset?.asset.__typename === "SampleAsset" ? asset.asset : undefined;
    const resolved = resolveDraggedBpm({
        reportedBpm: reported?.bpm,
        catalogBpm: base.bpm ?? undefined,
        catalogDuration_ms: sample?.duration ?? undefined,
        renderedDuration_s,
        declaredBpm: modified.bpm,
        isLoop: createInputAudioTypeFromAsset(asset) === AssetCategorySlug.Loop,
    });

    const wasPitchShifted =
        modified.wasPitchShifted ||
        (reported?.pitchSemitones !== undefined && reported.pitchSemitones !== 0);

    return {
        ...base,
        bpm: resolved.bpm,
        key: reported?.key ?? (wasPitchShifted ? null : base.key),
    };
};

const resolveFile = async (
    input: InputFile,
    dragged: DraggedAssets,
    getAudioDuration: GetAudioDuration,
): Promise<InputFile> => {
    if (input.metadata !== undefined) {
        return input;
    }

    const matched = findMatchedAsset(input.file, dragged);

    switch (input.type) {
        case "wav": {
            const metadata = createModifiedAssetMetadata(
                input.file.name,
                matched,
                await getAudioDuration(input.file),
            );
            return {
                file: input.file,
                type: "wav",
                metadata: {
                    ...metadata,
                    type: createInputAudioTypeFromAsset(matched.asset),
                },
            };
        }
        case "mid":
            // MIDI carries no audio to measure, so the catalog and the
            // filename are the only sources.
            return {
                file: input.file,
                type: "mid",
                metadata: createModifiedAssetMetadata(input.file.name, matched, undefined),
            };
        case "json":
            return input;
    }
};

export const createAddFiles = (
    streams: CoreStreams,
    addFiles: AddFiles,
    getAudioDuration: GetAudioDuration = decodeDuration,
): AddFiles => {
    // The host re-sends the hint once its modified render resolves, so the last
    // one before the drop is the one carrying the transform.
    let draggedAssets: DraggedAssets = { assets: [] };
    streams.assetsDragStarted((dragStarted) => {
        draggedAssets = dragStarted;
    });

    return async <T extends LabsResourceType>(files: InputFile<T>[]) => {
        const dragged = draggedAssets;
        draggedAssets = { assets: [] };

        const resolved = await Promise.all(
            files.map((f) => resolveFile(f, dragged, getAudioDuration)),
        );

        if (resolved.length === 0) {
            return [] as ResourceForType<T>[];
        }

        const result = await addFiles(resolved);

        if (result instanceof Error) {
            return result;
        }

        if (result.length === 0) {
            return new Error("Failed to load files");
        }

        return result as ResourceForType<T>[];
    };
};
